import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AddToFav } from './add-to-fav/add-to-fav';

@Component({
  selector: 'app-unit-gallery',
  standalone: true,
  imports: [CommonModule, AddToFav],
  template: `
    <div class="w-full min-w-0 flex flex-col">
      <div class="relative h-[300px] sm:h-[400px] lg:h-[500px] w-full bg-bg-secondary">
        @if (displayMedia.length && displayMedia[currentIndex].type === 'image') {
          <img
            [src]="displayMedia[currentIndex].url"
            [alt]="title"
            class="w-full h-full object-cover cursor-pointer"
            (click)="onImageClick()"
            (error)="handleImageError($event)"
          />
        } @else if (displayMedia.length && displayMedia[currentIndex].type === 'video') {
          <video
            [src]="displayMedia[currentIndex].url"
            controls
            class="w-full h-full object-cover"
          ></video>
        }
        
        <div class="absolute top-3 sm:top-4 left-3 sm:left-4 flex max-w-[calc(100%-6rem)] flex-col gap-2">
          @if (verified) {
            <div
              class="px-2 py-1 sm:px-2.5 sm:py-1.5 bg-success/90 backdrop-blur-md text-white text-[10px] sm:text-xs font-bold uppercase tracking-wider rounded-lg flex items-center gap-1.5 shadow-sm border border-success/50"
            >
              <i class="fa-solid fa-check-circle w-3 h-3 sm:w-3.5 sm:h-3.5"></i><span>Verified</span>
            </div>
          }
          @if (managed) {
            <div
              class="px-2 py-1 sm:px-2.5 sm:py-1.5 bg-info/90 backdrop-blur-md text-white text-[10px] sm:text-xs font-bold uppercase tracking-wider rounded-lg flex items-center gap-1.5 shadow-sm border border-info/50"
            >
              <i class="fa-solid fa-shield-alt w-3 h-3 sm:w-3.5 sm:h-3.5"></i><span>Managed</span>
            </div>
          }
        </div>
        
        <div class="absolute top-3 sm:top-4 right-3 sm:right-4 flex items-center gap-2">
          @if (unitId) {
            <app-add-to-fav [itemId]="unitId" [initialFavorite]="isFavorite"></app-add-to-fav>
          }
          <button
            (click)="shareProperty($event)"
            class="w-8 h-8 sm:w-9 sm:h-9 bg-bg-secondary/80 backdrop-blur-md rounded-xl flex items-center justify-center border border-border-subtle shadow-sm transition-all duration-300 hover:bg-surface-hover hover:border-primary/50 hover:shadow-primary-glow hover:-translate-y-0.5 active:scale-95"
            aria-label="Share property"
          >
            <i class="fa-solid fa-share-nodes text-[14px] text-text-secondary hover:text-primary transition-colors"></i>
          </button>
        </div>
        
        @if (displayMedia.length > 1) {
          <button
            (click)="previousImage()"
            class="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center bg-bg-base/60 backdrop-blur-md border border-border-subtle text-white rounded-full hover:bg-primary/90 hover:border-primary transition-all shadow-lg"
          >
            <i class="fa-solid fa-chevron-left text-sm"></i>
          </button>
          <button
            (click)="nextImage()"
            class="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center bg-bg-base/60 backdrop-blur-md border border-border-subtle text-white rounded-full hover:bg-primary/90 hover:border-primary transition-all shadow-lg"
          >
            <i class="fa-solid fa-chevron-right text-sm"></i>
          </button>
        }
        
        <div
          class="absolute bottom-3 sm:bottom-4 right-3 sm:right-4 px-3 py-1 bg-bg-base/70 backdrop-blur-md border border-border-subtle text-text-primary font-mono text-xs font-semibold rounded-lg shadow-sm"
        >
          {{ currentIndex + 1 }} / {{ displayMedia.length }}
        </div>
        
        @if (!displayMedia.length) {
          <div
            class="absolute bottom-3 sm:bottom-4 left-3 sm:left-4 px-3 py-1.5 bg-surface/90 backdrop-blur-md border border-border-subtle text-text-secondary text-xs font-medium rounded-lg"
          >
            No gallery media provided
          </div>
        }
      </div>
      
      <div class="p-2 sm:p-4 gap-2 sm:gap-3 overflow-x-auto flex bg-surface border-t border-border-subtle scrollbar-hide overscroll-x-contain">
        @for (item of displayMedia; track $index) {
          <button
            (click)="selectImage($index)"
            class="relative w-20 h-14 sm:w-32 sm:h-20 flex-shrink-0 rounded-lg sm:rounded-xl overflow-hidden transition-all duration-300 border-2"
            [class.border-border-subtle]="$index !== currentIndex"
            [class.opacity-60]="$index !== currentIndex"
            [class.hover:opacity-100]="$index !== currentIndex"
            [class.border-primary]="$index === currentIndex"
            [class.shadow-primary-glow]="$index === currentIndex"
          >
            @if (item.type === 'image') {
              <img
                [src]="item.url"
                alt="Gallery item {{ $index + 1 }}"
                class="w-full h-full object-cover"
                (error)="handleImageError($event)"
              />
            } @else {
              <div class="w-full h-full bg-bg-base flex items-center justify-center relative">
                 <video [src]="item.url" class="w-full h-full object-cover opacity-40"></video>
                 <i class="fa-solid fa-play text-white/90 absolute text-lg drop-shadow-lg"></i>
              </div>
            }
          </button>
        }
      </div>
    </div>
  `,
})
export class UnitGalleryComponent {
  private readonly fallbackImage =
    'https://images.unsplash.com/photo-1560185007-5f0bb1866cab?w=1200&h=800&fit=crop';

  @Input() images: string[] = [];
  @Input() videos: string[] = [];
  @Input() title: string = '';
  @Input() currentIndex: number = 0;
  @Input() verified: boolean = false;
  @Input() managed: boolean = false;
  @Input() unitId?: string;
  @Input() isFavorite: boolean = false;

  @Output() imageClick = new EventEmitter<void>();
  @Output() indexChange = new EventEmitter<number>();

  get displayMedia(): { type: 'image' | 'video'; url: string }[] {
    const media: { type: 'image' | 'video'; url: string }[] = [];
    
    // Add videos first
    if (this.videos && this.videos.length > 0) {
      this.videos.filter(Boolean).forEach(v => media.push({ type: 'video', url: this.sanitizeUrl(v) }));
    }
    
    // Then add images
    if (this.images && this.images.length > 0) {
      this.images.filter(Boolean).forEach(i => {
        const type = this.isVideoUrl(i) ? 'video' : 'image';
        media.push({ type, url: this.sanitizeUrl(i) });
      });
    }

    if (media.length === 0) {
      media.push({ type: 'image', url: this.fallbackImage });
    }
    
    return media;
  }

  sanitizeUrl(url: string): string {
    if (!url) return this.fallbackImage;
    let clean = url.trim();
    if (clean.startsWith('/uploads') || clean.startsWith('uploads/')) {
      const base = 'https://api.homigo-eg.com';
      clean = clean.startsWith('/') ? `${base}${clean}` : `${base}/${clean}`;
    }
    try {
      clean = encodeURI(clean);
    } catch {
      // ignore
    }
    return clean;
  }

  isVideoUrl(url: string): boolean {
    if (!url) return false;
    return !!url.match(/\.(mp4|webm|ogg|mov)$/i) || url.includes('/video/upload/');
  }

  handleImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    if (img && img.src !== this.fallbackImage) {
      img.src = this.fallbackImage;
    }
  }

  selectImage(index: number): void {
    if (index < 0 || index >= this.displayMedia.length) return;
    this.currentIndex = index;
    this.indexChange.emit(index);
  }

  previousImage(): void {
    const totalImages = this.displayMedia.length;
    if (totalImages <= 1) return;
    this.currentIndex = this.currentIndex === 0 ? totalImages - 1 : this.currentIndex - 1;
    this.indexChange.emit(this.currentIndex);
  }

  nextImage(): void {
    const totalImages = this.displayMedia.length;
    if (totalImages <= 1) return;
    this.currentIndex = (this.currentIndex + 1) % totalImages;
    this.indexChange.emit(this.currentIndex);
  }

  onImageClick(): void {
    this.imageClick.emit();
  }

  async shareProperty(event: Event): Promise<void> {
    event.stopPropagation();
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({
          title: this.title,
          url: url
        });
      } catch (err) {
        console.error('Error sharing:', err);
      }
    } else {
      navigator.clipboard.writeText(url).then(() => {
        alert('تم نسخ الرابط بنجاح!');
      }).catch(err => {
        console.error('Failed to copy link:', err);
      });
    }
  }
}
